import { useState, useMemo } from "react";
import reduce from "lodash/reduce";
import { HTTPMethod } from "@merge-api/merge-javascript-shared";
import {
  Destination,
  DestinationAuthSchemaFieldType,
} from "../../../../../../../../models/Entities";
import { fetchWithAuth } from "../../../../../../../../api-client/APIClient";
import { isValidJSON, isValidHostname } from "../../../../../../../../services";
import useAppContext from "../../../../../../../context/useAppContext";

type UseCreateSyncConnectionProps = {
  destination: Destination;
  onNext: (error?: { message: string; third_party_error: boolean }) => void;
  authFields: {
    [key: string]: string;
  };
  symmetricDataKey: string | undefined;
};

const useCreateSyncConnection = ({
  onNext,
  authFields,
  destination,
  symmetricDataKey,
}: UseCreateSyncConnectionProps) => {
  const { user } = useAppContext();
  // state
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // derived state
  const requiredFields = useMemo(() => {
    const authSchema = destination.auth_schema;
    return authSchema
      .flatMap(({ fields }) => fields)
      .filter(({ required }) => required)
      .map(({ key }) => key);
  }, [destination.auth_schema]);

  const jsonFields = useMemo(() => {
    const authSchema = destination.auth_schema;
    return authSchema
      .flatMap(({ fields }) => fields)
      .filter(({ type }) => type === DestinationAuthSchemaFieldType.JSON)
      .map(({ key }) => key);
  }, [destination.auth_schema]);

  const hostFields = useMemo(() => {
    const authSchema = destination.auth_schema;
    return authSchema
      .flatMap(({ fields }) => fields)
      .filter(({ key }) => key === "host")
      .map(({ key }) => key);
  }, [destination.auth_schema]);

  const isSubmitDisabled = useMemo(
    () =>
      // all required fields exist
      requiredFields.some((key) => !authFields[key]) ||
      // all json fields are valid json (or do not exist, which is fine unless required and would be caught by above line)
      jsonFields.some((key) => authFields[key] && !isValidJSON(authFields[key])) ||
      // all host fields are valid hostnames
      hostFields.some((key) => authFields[key] && !isValidHostname(authFields[key])),
    [authFields, hostFields, jsonFields, requiredFields],
  );

  const onSubmit = () => {
    setIsSubmitLoading(true);

    // flatten JSON
    // get list of all fields of type === JSON
    const jsonFields = destination.auth_schema
      .flatMap(({ fields }) => fields)
      .filter(({ type }) => type === DestinationAuthSchemaFieldType.JSON)
      .map(({ key }) => key);

    // flatten all JSON fields into authFields array
    const flattenedAuthFields = reduce(
      authFields,
      (acc, curr, key) => {
        if (jsonFields.includes(key) && isValidJSON(curr)) {
          const parsedJson = JSON.parse(curr);
          acc = { ...acc, ...parsedJson };
        } else {
          acc[key] = curr;
        }

        return acc;
      },
      {} as any,
    );

    fetchWithAuth({
      path: "/destinations/create-customer-linked-destination",
      method: HTTPMethod.POST,
      body: {
        auth_fields: flattenedAuthFields,
        destination_id: destination.id,
        organization_id: user.organization.id,
        symmetric_data_key: symmetricDataKey,
      },
      onResponse: () => {
        setIsSubmitLoading(false);
        onNext();
      },
      onError: async (e: any) => {
        setIsSubmitLoading(false);
        try {
          const body = await e.json();
          if ("message" in body) {
            onNext(body);
          } else {
            onNext({
              message: "Error connecting destination! Please try again.",
              third_party_error: false,
            });
          }
        } catch (e) {
          onNext({
            message: "Error connecting destination! Please try again.",
            third_party_error: false,
          });
        }
      },
    });
  };

  return { onSubmit, isSubmitLoading, isSubmitDisabled };
};

export default useCreateSyncConnection;
